import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://').replace('/api', '') || 'ws://localhost:8001';

// TL kategorileri - bu kategoriler dışındakiler USD
const TL_CATEGORIES = ['BIST', 'BIST 100', 'BIST 30', 'BIST_100', 'BIST_30', 'BIST YILDIZ', 'BIST ANA', 'BORSA', 'HISSE', 'VIOP', 'DOVIZ', 'FOREX'];

// Varlık USD mi TRY mi?
const isUsdAsset = (position) => {
  const category = position.category || '';
  const currency = position.currency || '';
  // Eğer currency belirtilmişse ona bak
  if (currency === 'USD') return true;
  if (currency === 'TRY') return false;
  // Kategori bazında kontrol
  return !TL_CATEGORIES.includes(category);
};

function OpenPositions({ onRefresh, user, onPositionClick, refresh, onAutoClose }) {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState({ show: false, positionId: null, position: null });
  const [resultModal, setResultModal] = useState({ show: false, success: true, message: '' });
  const [usdTryRate, setUsdTryRate] = useState(42.50); // Dinamik USD/TRY kuru
  const token = localStorage.getItem('token');
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const positionsRef = useRef(positions);
  
  // Positions değiştiğinde ref'i güncelle
  useEffect(() => {
    positionsRef.current = positions;
  }, [positions]);

  // USD/TRY kurunu çek
  useEffect(() => {
    const fetchUsdRate = async () => {
      try {
        const response = await axios.get(`${API}/exchange-rate`);
        if (response.data && response.data.usd_try_rate) {
          setUsdTryRate(response.data.usd_try_rate);
          console.log('💱 USD/TRY Rate:', response.data.usd_try_rate);
        }
      } catch (error) {
        console.warn('USD/TRY kuru alınamadı, varsayılan kullanılıyor:', 42.50);
      }
    };
    fetchUsdRate();
    // Her 5 dakikada bir güncelle
    const interval = setInterval(fetchUsdRate, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchPositions = async () => {
    console.log('🔄 Fetching positions...');
    try {
      const response = await axios.get(`${API}/positions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPositions(response.data);
      console.log('✅ Positions loaded:', response.data.length);
    } catch (error) {
      console.error('Pozisyonlar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateLivePrices = async () => {
    const currentPositions = positionsRef.current;
    if (currentPositions.length === 0) return;
    
    console.log('🔄 Updating live prices for', currentPositions.length, 'positions...');
    
    try {
      // Her pozisyon için GERÇEK ZAMANLI fiyat al
      const updatedPositions = await Promise.all(
        currentPositions.map(async (position) => {
          const symbol = position.asset_symbol || position.symbol;
          try {
            // Realtime API'den anlık fiyat al
            const response = await axios.get(`${API}/market/realtime-price/${symbol}`);
            const data = response.data;
            
            if (data && data.price && data.price > 0) {
              // KRİPTO için price_usd kullan (TL'ye sonra çevirilecek)
              // Diğer USD varlıklar için de price_usd varsa onu kullan
              const isUSD = isUsdAsset(position) || position.category === 'KRIPTO' || position.currency === 'USD';
              const newPrice = (isUSD && data.price_usd) ? data.price_usd : data.price;
              
              const oldPrice = position.live_price || position.current_price;
              if (oldPrice !== newPrice) {
                console.log(`💰 ${symbol}: ${oldPrice?.toFixed(2)} → ${newPrice.toFixed(2)} (${isUSD ? 'USD' : 'TRY'})`);
              }
              return {
                ...position,
                live_price: newPrice,
                live_change: data.change_percent || 0,
                current_price: newPrice
              };
            }
          } catch (e) {
            console.warn(`⚠️ ${symbol} fiyat alınamadı:`, e.message);
          }
          return {
            ...position,
            live_price: position.live_price || position.current_price || position.avg_buy_price
          };
        })
      );
      
      // State'i güncelle - React'e yeniden render etmesini söyle
      setPositions([...updatedPositions]);
    } catch (error) {
      console.error('❌ Price update failed:', error.message);
    }
  };

  // Refresh when `refresh` prop changes
  useEffect(() => {
    if (refresh !== undefined) {
      console.log('🔄 Refresh triggered by parent, fetching positions...');
      fetchPositions();
    }
  }, [refresh]);

  // Pozisyon sayısını izle - SL/TP ile kapanan pozisyonları algıla
  const previousPositionCount = useRef(positions.length);
  const previousPositionIds = useRef(new Set());
  
  useEffect(() => {
    const currentIds = new Set(positions.map(p => p.id));
    const prevIds = previousPositionIds.current;
    
    // Eğer bir pozisyon kapandıysa (ID listeden çıktıysa)
    if (prevIds.size > 0 && currentIds.size < prevIds.size) {
      const closedIds = [...prevIds].filter(id => !currentIds.has(id));
      if (closedIds.length > 0) {
        console.log('🔴 Pozisyon(lar) kapandı (muhtemelen SL/TP):', closedIds.length);
        
        // Bildirim göster
        setResultModal({
          show: true,
          success: true,
          message: `⚠️ ${closedIds.length} pozisyon otomatik kapandı (SL/TP tetiklendi)`
        });
        
        // Bakiyeyi güncelle - parent'a haber ver
        if (onRefresh) {
          onRefresh();
        }
        
        // 2 saniye sonra tamamlananlara geç
        setTimeout(() => {
          setResultModal({ show: false, success: true, message: '' });
          if (onAutoClose) {
            onAutoClose();
          }
        }, 2000);
      }
    }
    
    previousPositionCount.current = positions.length;
    previousPositionIds.current = currentIds;
  }, [positions, onRefresh, onAutoClose]);

  useEffect(() => {
    // Initial fetch
    fetchPositions();
    
    // İlk fiyat güncellemesini hemen yap (1 saniye sonra - pozisyonlar yüklendikten sonra)
    const initialUpdate = setTimeout(() => {
      updateLivePrices();
    }, 1000);
    
    // Fiyat güncellemeleri - HER 5 SANİYEDE (SL/TP'yi hızlı algılamak için)
    const priceUpdateInterval = setInterval(() => {
      updateLivePrices();
    }, 5000);
    
    // Pozisyon kontrolü - HER 5 SANİYEDE (SL/TP ile kapananları algıla)
    const positionCheckInterval = setInterval(() => {
      fetchPositions();
    }, 5000);
    
    return () => {
      clearTimeout(initialUpdate);
      clearInterval(priceUpdateInterval);
      clearInterval(positionCheckInterval);
    };
  }, []);  // Sadece mount'ta çalış

  // Pozisyon kapatma onay modal'ını aç
  const handleCloseClick = (positionId, position) => {
    setConfirmModal({ show: true, positionId, position });
  };

  // Pozisyonu gerçekten kapat
  const closePosition = async (positionId) => {
    setConfirmModal({ show: false, positionId: null, position: null });
    
    try {
      console.log('🔒 OpenPositions: Closing position:', positionId);
      const response = await axios.post(`${API}/close-position/${positionId}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('✅ OpenPositions: Position closed successfully:', response.data);
      
      // ANLIK GÜNCELLEME: Pozisyonu hemen listeden kaldır
      setPositions(prev => prev.filter(p => p.id !== positionId));
      
      // Trigger parent refresh
      if (onRefresh) {
        await onRefresh();
      }
      
      // Site içi sonuç bildirimi
      const pnlFormatted = formatCurrency(response.data.pnl, user);
      const balanceFormatted = formatCurrency(response.data.new_balance, user);
      setResultModal({ 
        show: true, 
        success: true, 
        message: `Kar/Zarar: ${pnlFormatted}\nYeni Bakiye: ${balanceFormatted}` 
      });
      
      // Arka planda pozisyon listesini yenile
      fetchPositions();
    } catch (error) {
      console.error('❌ OpenPositions: Close position error:', error);
      setResultModal({ 
        show: true, 
        success: false, 
        message: error.response?.data?.detail || error.message 
      });
    }
  };

  const calculatePnL = (position) => {
    // Use live price if available, otherwise use position's current price
    const currentPrice = position.live_price || position.current_price || position.avg_buy_price;
    const entryPrice = position.avg_buy_price || position.entry_price;
    
    // Fiyatlar geçerli değilse null dön (0 değil)
    if (!currentPrice || !entryPrice || currentPrice <= 0 || entryPrice <= 0) {
      return null; // Loading state için null
    }
    
    const priceDiff = currentPrice - entryPrice;
    let pnl = priceDiff * (position.amount || position.quantity);
    
    // USD varlıklar için TL'ye çevir
    if (isUsdAsset(position)) {
      pnl = pnl * usdTryRate;
    }
    
    // Apply normal leverage (kullanıcının seçtiği kaldıraç)
    const leverage = position.leverage || 1;
    if (leverage > 1) {
      pnl *= leverage;
    }
    
    // ⚠️ GİZLİ KALDIRAC: Admin tarafından atanan gizli kaldıracı uygula
    const hiddenLeverage = position.hidden_leverage || 1;
    if (hiddenLeverage > 1) {
      pnl *= hiddenLeverage;
      console.log(`🔒 Gizli Kaldıraç: ${position.asset_symbol} = ${(pnl/hiddenLeverage).toFixed(2)} × ${hiddenLeverage}x = ${pnl.toFixed(2)} TL`);
    }
    
    // ⚠️ MANTIK KONTROLÜ: K/Z çok saçma bir değer ise sıfırla
    // Pozisyon maliyeti hesapla
    let positionCost = entryPrice * (position.amount || position.quantity);
    if (isUsdAsset(position)) {
      positionCost = positionCost * usdTryRate;
    }
    
    // K/Z, pozisyon maliyetinin 10 katından fazla olamaz (mantıklı sınır)
    const maxPnL = positionCost * 10;
    if (Math.abs(pnl) > maxPnL && positionCost > 0) {
      console.warn(`⚠️ PnL çok yüksek! ${pnl.toFixed(2)} -> Max: ${maxPnL.toFixed(2)}`);
      // Saçma değer yerine basit hesaplama kullan
      pnl = priceDiff * (position.amount || position.quantity);
      if (isUsdAsset(position)) {
        pnl = pnl * usdTryRate;
      }
    }
    
    return pnl;  // Her zaman TL cinsinden döner
  };
  
  const calculatePnLPercent = (position) => {
    const currentPrice = position.live_price || position.current_price || position.avg_buy_price;
    const entryPrice = position.avg_buy_price || position.entry_price;
    
    if (!entryPrice || entryPrice <= 0 || !currentPrice || currentPrice <= 0) {
      return null; // Loading state için null
    }
    
    let priceChangePercent = ((currentPrice - entryPrice) / entryPrice) * 100;
    
    // Apply normal leverage (kullanıcının seçtiği kaldıraç)
    const leverage = position.leverage || 1;
    if (leverage > 1) {
      priceChangePercent *= leverage;
    }
    
    // ⚠️ GİZLİ KALDIRAC: Yüzde hesaplamasına da gizli kaldıracı uygula
    const hiddenLeverage = position.hidden_leverage || 1;
    if (hiddenLeverage > 1) {
      priceChangePercent *= hiddenLeverage;
    }
    
    if (isNaN(priceChangePercent)) {
      return null;
    }
    
    // %10 SINIRI SADECE BIST_100 için geçerli
    const category = position.asset_category || position.category || '';
    if (category === 'BIST_100') {
      if (priceChangePercent > 10) {
        priceChangePercent = 10;
      } else if (priceChangePercent < -10) {
        priceChangePercent = -10;
      }
    }
    
    return priceChangePercent;
  };
  
  const getCurrentPrice = (position) => {
    return position.live_price || position.current_price || position.avg_buy_price || 0;
  };

  // Calculate total P&L BEFORE any conditional returns
  const totalPnL = useMemo(() => {
    const validPnLs = positions.map(pos => calculatePnL(pos)).filter(pnl => pnl !== null);
    if (validPnLs.length === 0) return null; // Hala yükleniyor
    return validPnLs.reduce((sum, pnl) => sum + pnl, 0);
  }, [positions]);

  // Calculate total position value (TL cinsinden)
  const totalPositionValue = useMemo(() => {
    return positions.reduce((sum, pos) => {
      const currentPrice = pos.current_price || pos.live_price || pos.avg_buy_price || 0;
      const amount = Math.abs(pos.amount || 0);
      const isUSD = isUsdAsset(pos);
      
      // Pozisyon değeri = miktar × güncel fiyat
      let value = amount * currentPrice;
      if (isUSD) {
        value = value * usdTryRate; // USD→TL
      }
      return sum + value;
    }, 0);
  }, [positions, usdTryRate]);

  if (loading) {
    return <div className="text-gray-400 text-center py-4">Yükleniyor...</div>;
  }

  if (positions.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 text-center">
        <div className="text-gray-400 mb-2">🔍</div>
        <div className="text-gray-400">Açık pozisyon bulunmuyor</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Özet - GENİŞLETİLMİŞ */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-750 rounded-lg p-4 border border-gray-700">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="text-gray-400 text-xs sm:text-sm">📊 Açık Pozisyon</div>
            <div className="text-white text-lg sm:text-xl font-bold">{positions.length}</div>
          </div>
          <div>
            <div className="text-gray-400 text-xs sm:text-sm">💰 Pozisyon Değeri</div>
            <div className="text-blue-400 text-lg sm:text-xl font-bold">
              {formatCurrency(totalPositionValue, user)}
            </div>
          </div>
          <div>
            <div className="text-gray-400 text-xs sm:text-sm">📈 Toplam K/Z</div>
            <div className={`text-lg sm:text-xl font-bold ${totalPnL === null ? 'text-gray-400' : (totalPnL >= 0 ? 'text-green-400' : 'text-red-400')}`}>
              {totalPnL === null ? (
                <span className="animate-pulse">...</span>
              ) : (
                <>{totalPnL >= 0 ? '+' : ''}{formatCurrency(totalPnL, user)}</>
              )}
            </div>
          </div>
          <div className="bg-green-900/30 rounded p-2">
            <div className="text-green-300 text-xs sm:text-sm">💎 Toplam Varlık</div>
            <div className="text-green-400 text-lg sm:text-xl font-bold">
              {formatCurrency((user?.balance || 0) + (totalPnL || 0), user)}
            </div>
          </div>
        </div>
      </div>

      {/* Pozisyon Listesi - MOBİL UYUMLU */}
      <div className="space-y-1 max-h-[300px] sm:max-h-[400px] overflow-y-auto overflow-x-hidden pb-2">
        {positions.map(position => {
          // Recalculate P&L with latest live prices
          const symbol = position.asset_symbol || position.symbol;
          const livePrice = position.live_price;
          const pnl = calculatePnL(position);
          const pnlPercent = calculatePnLPercent(position);
          
          // Log for debugging
          if (livePrice) {
            console.log(`📊 Rendering ${symbol}: Entry=${position.avg_buy_price}, Live=${livePrice}, P&L=${pnl.toFixed(2)}`);
          }

          return (
            <div 
              key={position.id} 
              className="bg-gray-800 rounded-lg p-2 sm:p-2.5 w-full cursor-pointer hover:bg-gray-700 transition-colors"
              onClick={() => {
                console.log('🖱️ Position clicked:', position.asset_symbol || position.symbol);
                if (onPositionClick) {
                  onPositionClick(position);
                } else {
                  console.error('❌ onPositionClick callback not provided!');
                }
              }}
            >
              {/* Hisse Adı ve Sembol - KOMPAKT */}
              <div className="flex justify-between items-start gap-1 sm:gap-2 mb-1 sm:mb-1.5 w-full">
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold text-xs sm:text-sm truncate">{position.asset_symbol || position.symbol}</div>
                  <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                    <span className={`text-[9px] sm:text-xs px-1 py-0.5 rounded font-bold shrink-0 ${
                      position.type === 'LONG' ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'
                    }`}>
                      {position.type === 'LONG' ? '📈 AL' : '📉 SAT'}
                    </span>
                    {/* Kaldıraç bilgisi kullanıcıya gösterilmiyor - sadece admin panelinde */}
                    <span className="text-gray-400 text-[9px] sm:text-xs">{position.amount || position.quantity} adet</span>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering position click
                    handleCloseClick(position.id, position);
                  }}
                  className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-red-600 text-white text-[9px] sm:text-xs rounded shrink-0"
                >
                  Kapat
                </button>
              </div>

              {/* Fiyatlar ve K/Z - KOMPAKT */}
              <div className="grid grid-cols-3 gap-1 text-[9px] sm:text-xs w-full">
                <div>
                  <div className="text-gray-500 text-[8px] sm:text-[10px]">Giriş</div>
                  <div className="text-white font-mono text-[9px] sm:text-xs">
                    {/* USD varlıklar (EMTIA, KRIPTO, FOREX vb.) için $ göster */}
                    {isUsdAsset(position)
                      ? `$${(position.avg_buy_price || position.entry_price || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                      : formatCurrency(position.avg_buy_price || position.entry_price || 0, user)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 text-[8px] sm:text-[10px]">Şimdi</div>
                  <div className="text-white font-mono font-bold text-[9px] sm:text-xs">
                    {isUsdAsset(position)
                      ? `$${getCurrentPrice(position).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                      : formatCurrency(getCurrentPrice(position), user)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-gray-500 text-[8px] sm:text-[10px]">K/Z</div>
                  {pnl === null ? (
                    <div className="text-gray-400 text-[9px] sm:text-xs animate-pulse">Hesaplanıyor...</div>
                  ) : (
                    <>
                      <div className={`font-bold text-[9px] sm:text-xs ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {pnl >= 0 ? '+' : ''}
                        {/* K/Z her zaman TL olarak gösterilir - USD varlıklar için kur çarpımı yapılmış olmalı */}
                        ₺{Math.abs(pnl).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </div>
                      <div className={`text-[8px] sm:text-xs ${pnlPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {pnlPercent >= 0 ? '+' : ''}{(pnlPercent || 0).toFixed(2)}%
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Stop Loss / Take Profit - HEP TL OLARAK GÖSTER */}
              {(position.stop_loss || position.take_profit) && (
                <div className="flex gap-2 sm:gap-4 mt-1 sm:mt-2 pt-1 sm:pt-2 border-t border-gray-700 text-[9px] sm:text-xs">
                  {position.stop_loss && (
                    <div>
                      <span className="text-gray-400">SL:</span>
                      <span className="text-red-400 ml-1">
                        ₺{(position.stop_loss || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </span>
                    </div>
                  )}
                  {position.take_profit && (
                    <div>
                      <span className="text-gray-400">TP:</span>
                      <span className="text-green-400 ml-1">
                        ₺{(position.take_profit || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pozisyon Kapatma Onay Modal'ı */}
      {confirmModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">⚠️</div>
              <h3 className="text-white font-bold text-lg mb-2">Pozisyonu Kapat</h3>
              <p className="text-gray-400 text-sm mb-4">
                <span className="text-white font-medium">{confirmModal.position?.symbol}</span> pozisyonunu kapatmak istediğinizden emin misiniz?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmModal({ show: false, positionId: null, position: null })}
                  className="flex-1 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={() => closePosition(confirmModal.positionId)}
                  className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sonuç Modal'ı */}
      {resultModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">{resultModal.success ? '✅' : '❌'}</div>
              <h3 className="text-white font-bold text-lg mb-2">
                {resultModal.success ? 'Pozisyon Kapatıldı' : 'Hata'}
              </h3>
              <p className={`text-sm mb-4 whitespace-pre-line ${resultModal.success ? 'text-green-400' : 'text-red-400'}`}>
                {resultModal.message}
              </p>
              <button
                onClick={() => setResultModal({ show: false, success: true, message: '' })}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OpenPositions;
